import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../components/Config/api.service";
import { notify } from "../../components/Utils/Notifications";
import HeaderCard from "../../components/Header/HeaderCard";
import { motion } from "framer-motion";
import { FaFileExcel, FaUpload, FaSave, FaCalculator, FaTimes, FaArrowLeft } from "react-icons/fa";
import * as XLSX from "xlsx";
import ImportMappingModal from "../../components/Tools/ImportMappingModal";
import SheetSelectionModal from "../../components/Tools/SheetSelectionModal";
import { useExcelMappingImport } from "../../components/Tools/useExcelMappingImport";
import { findInputIndex } from "./findInputIndex";

const DailySalaryInputs = () => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';
    const today = new Date();
    const currentMonthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

    const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
    const [loading, setLoading] = useState(false);
    const [inputs, setInputs] = useState([]);
    const [dragActive, setDragActive] = useState(false);

    // UX and Performance Optimizations for large lists (500+ employees)
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // "all", "modified", "unmodified"
    const [departmentFilter, setDepartmentFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const importInputRef = useRef(null);
    const navigate = useNavigate();

    // Convert monthly selection (YYYY-MM) to first day date (YYYY-MM-01)
    const getTargetDate = (monthStr) => {
        return `${monthStr}-01`;
    };

    // Nombre de jours du mois sélectionné : borne haute des jours travaillés.
    const getDaysInMonth = (monthStr) => {
        const [year, month] = monthStr.split("-").map(Number);
        return new Date(year, month, 0).getDate();
    };

    // Règle commune à la saisie manuelle et à l'import : les attributs min et max
    // des champs sont inertes ici, l'écran enregistrant sans balise <form>.
    const isValidInput = (workedDays, grossSalary, monthStr) => {
        const maxDays = getDaysInMonth(monthStr);
        return (
            Number.isFinite(workedDays) && workedDays >= 0 && workedDays <= maxDays &&
            Number.isInteger(workedDays * 2) &&
            Number.isFinite(grossSalary) && grossSalary >= 0
        );
    };

    const fetchInputs = useCallback(async (monthStr) => {
        setLoading(true);
        try {
            const date = getTargetDate(monthStr);
            const response = await apiService.getElement(`/daily-salary-inputs?date=${date}`);
            if (response.data && response.data.inputs) {
                setInputs(response.data.inputs);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des saisies :", error);
            notify(t('dailySalaryInputs.loadError'), "", 3);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInputs(selectedMonth);
    }, [selectedMonth, fetchInputs]);

    // Handle manual inline edit of days or gross using unique employee ID
    const handleValueChange = (employeeId, field, value) => {
        const parsedValue = value === "" ? "" : parseFloat(value);
        setInputs((prev) => {
            const updated = [...prev];
            const matchIdx = updated.findIndex(item => item.employee_id === employeeId);
            if (matchIdx !== -1) {
                updated[matchIdx] = {
                    ...updated[matchIdx],
                    [field]: parsedValue,
                    is_modified: true
                };
            }
            return updated;
        });
    };

    // Departments present in the currently loaded inputs, for the filter dropdown
    const departmentOptions = React.useMemo(() => {
        const seen = new Map();
        inputs.forEach(item => {
            if (item.department && !seen.has(item.department.id)) {
                seen.set(item.department.id, item.department.name);
            }
        });
        return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [inputs]);

    // Filter inputs based on search query, status filter and department filter
    const filteredInputs = React.useMemo(() => {
        return inputs.filter(item => {
            const matchesSearch =
                String(item.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                String(item.matricule || "").toLowerCase().includes(searchQuery.toLowerCase());

            const matchesStatus =
                statusFilter === "all" ||
                (statusFilter === "modified" && item.is_modified) ||
                (statusFilter === "unmodified" && !item.is_modified);

            const matchesDepartment =
                departmentFilter === "all" ||
                String(item.department?.id) === departmentFilter;

            return matchesSearch && matchesStatus && matchesDepartment;
        });
    }, [inputs, searchQuery, statusFilter, departmentFilter]);

    // Paginate filtered inputs
    const paginatedInputs = React.useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredInputs.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredInputs, currentPage]);

    const totalPages = Math.ceil(filteredInputs.length / itemsPerPage);

    // Reset page on filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, statusFilter, departmentFilter]);

    const applyImportedRows = (importedRows) => {
        let updateCount = 0;
        let rejectedCount = 0;
        setInputs((prev) => {
            const updated = [...prev];
            importedRows.forEach(row => {
                if (!isValidInput(row.worked_days, row.gross_salary_input, selectedMonth)) {
                    rejectedCount++;
                    return;
                }
                const matchIdx = findInputIndex(updated, row);

                if (matchIdx !== -1) {
                    updated[matchIdx] = {
                        ...updated[matchIdx],
                        worked_days: row.worked_days,
                        gross_salary_input: row.gross_salary_input,
                        is_modified: true
                    };
                    updateCount++;
                }
            });
            return updated;
        });

        if (updateCount > 0) {
            notify(t('dailySalaryInputs.importSuccess', { count: updateCount }), "", 2);
        } else if (rejectedCount === 0) {
            notify(t('dailySalaryInputs.importNoMatch'), "", 3);
        }
        if (rejectedCount > 0) {
            notify(t('dailySalaryInputs.importRejected', {
                count: rejectedCount,
                maxDays: getDaysInMonth(selectedMonth)
            }), "", 3);
        }
    };

    const excelImport = useExcelMappingImport({
        notify,
        modelFields: [
            { name: "matricule", string: t('dailySalaryInputs.matricule') },
            { name: "name", string: t('dailySalaryInputs.employeeName') },
            { name: "worked_days", string: t('dailySalaryInputs.workedDays') },
            { name: "gross_salary_input", string: t('dailySalaryInputs.monthGross') },
        ],
        templateHeaders: [t('dailySalaryInputs.matricule'), t('dailySalaryInputs.employeeName'), t('dailySalaryInputs.workedDays'), t('dailySalaryInputs.monthGross')],
        templateSheetName: "Import Journaliers",
        templateFilename: "Template_Saisie_Journaliers.xlsx",
        mapMappedRow: (row) => ({
            matricule: String(row.matricule || "").trim(),
            name: String(row.name || "").trim(),
            worked_days: parseFloat(row.worked_days),
            gross_salary_input: parseFloat(row.gross_salary_input),
        }),
        isMappedRowValid: (row) => (row.matricule || row.name) && !isNaN(row.worked_days) && !isNaN(row.gross_salary_input),
        onImportRows: applyImportedRows,
    });

    // Drag & Drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const fakeEvent = {
                target: {
                    files: e.dataTransfer.files,
                    value: null
                }
            };
            excelImport.handleImportFileChange(fakeEvent);
        }
    };

    const handleFileChange = (e) => {
        excelImport.handleImportFileChange(e);
    };

    // Submit inputs to backend
    const handleSave = async () => {
        setLoading(true);
        try {
            const payload = {
                date: getTargetDate(selectedMonth),
                inputs: inputs.filter(item => item.is_modified).map(item => ({
                    matricule: item.matricule,
                    name: item.name,
                    worked_days: item.worked_days,
                    gross_salary_input: item.gross_salary_input
                }))
            };

            if (payload.inputs.length === 0) {
                notify(t('dailySalaryInputs.noChangesToSave'), "", 1);
                setLoading(false);
                return;
            }

            // Le tableau étant paginé et filtré, on envoie des lignes que
            // l'utilisateur n'a pas forcément sous les yeux : on les vérifie toutes.
            const invalid = payload.inputs.filter(
                item => !isValidInput(item.worked_days, item.gross_salary_input, selectedMonth)
            );
            if (invalid.length > 0) {
                notify(t('dailySalaryInputs.invalidValues', {
                    count: invalid.length,
                    maxDays: getDaysInMonth(selectedMonth),
                    list: invalid.slice(0, 5).map(item => item.matricule || item.name).join(', ')
                        + (invalid.length > 5 ? '...' : '')
                }), "", 3);
                setLoading(false);
                return;
            }

            const response = await apiService.saveElement("/daily-salary-inputs/import", payload);
            if (response.data && response.data.success) {
                notify(t('dailySalaryInputs.saveSuccess'), "", 2);
                fetchInputs(selectedMonth);
            } else if (response.data && response.data.error) {
                notify(response.data.error, "", 3);
            }
        } catch (error) {
            console.error("Erreur lors de la sauvegarde :", error);
            notify(t('dailySalaryInputs.saveError'), "", 3);
        } finally {
            setLoading(false);
        }
    };



    return (
        <>
            <SheetSelectionModal
                isOpen={excelImport.showSheetSelection}
                sheetNames={excelImport.sheetNames}
                onSelect={excelImport.handleSheetSelect}
                onCancel={excelImport.handleCancelSheetSelection}
            />
            {excelImport.showMapping && (
                <ImportMappingModal
                    columns={excelImport.fileColumns}
                    modelFields={excelImport.modelFields}
                    onDownloadTemplate={excelImport.handleDownloadTemplate}
                    onConfirm={excelImport.handleConfirmMapping}
                    onTest={excelImport.handleTestMapping}
                    onCancel={excelImport.handleCancelMapping}
                />
            )}
            <div className="container mx-auto p-6 space-y-6">
                <HeaderCard
                    title={t('dailySalaryInputs.title')}
                    text={t('dailySalaryInputs.subtitle')}
                    goBack={() => navigate('/payslip/report')}
                    rightActions={
                        <div className="flex gap-2">
                            <button
                                onClick={handleSave}
                                disabled={loading || !inputs.some(i => i.is_modified)}
                                className="flex items-center gap-2 px-5 py-2.5 bg-[--color-primary] hover:bg-[--bg-hover] disabled:bg-gray-300 text-white font-semibold rounded-lg transition-all shadow-md duration-200 text-sm"
                            >
                                <FaSave size={16} />
                                {t('dailySalaryInputs.save')}
                            </button>
                        </div>
                    }
                />

                {/* Selection & Actions Bar */}
                <div className="flex flex-wrap gap-4 items-center justify-between bg-white p-6 rounded-xl shadow-md border border-gray-100">
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex flex-col">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('dailySalaryInputs.payMonth')}</label>
                            <input
                                type="month"
                                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] text-gray-700 bg-gray-50 font-medium"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-col">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('dailySalaryInputs.search')}</label>
                            <input
                                type="text"
                                placeholder={t('dailySalaryInputs.searchPlaceholder')}
                                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] text-gray-700 bg-gray-50 font-medium w-64"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-col">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('dailySalaryInputs.filterBy')}</label>
                            <select
                                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] text-gray-700 bg-gray-50 font-medium w-40"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">{t('dailySalaryInputs.all')}</option>
                                <option value="modified">{t('dailySalaryInputs.modified')}</option>
                                <option value="unmodified">{t('dailySalaryInputs.unmodified')}</option>
                            </select>
                        </div>

                        <div className="flex flex-col">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('dailySalaryInputs.filterByDepartment')}</label>
                            <select
                                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] text-gray-700 bg-gray-50 font-medium w-52"
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                            >
                                <option value="all">{t('dailySalaryInputs.allDepartments')}</option>
                                {departmentOptions.map(dept => (
                                    <option key={dept.id} value={String(dept.id)}>{dept.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={excelImport.handleDownloadTemplate}
                            className="flex items-center gap-2 px-5 py-2.5 bg-[--color-primary] hover:bg-[--color-primary-light] text-white rounded-lg transition-all shadow-sm font-medium duration-200"
                        >
                            <FaFileExcel size={18} />
                            {t('dailySalaryInputs.excelTemplate')}
                        </button>
                    </div>
                </div>

                {/* Drag & Drop Zone */}
                <div
                    onDragEnter={handleDrag}
                    onDragOver={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                    className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${dragActive ? "border-[--color-primary] bg-[--color-primary-light]" : "border-[--color-primary] bg-white hover:bg-white"
                        }`}
                >
                    <input
                        type="file"
                        id="excel-upload"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <label htmlFor="excel-upload" className="cursor-pointer flex flex-col items-center space-y-3">
                        <div className="p-4 bg-[--bg-color] text-[--color-primary] rounded-full">
                            <FaUpload size={28} />
                        </div>
                        <div>
                            <span className="text-[--color-primary] font-semibold hover:underline">{t('dailySalaryInputs.clickToImport')}</span>
                            <span className="text-gray-500"> {t('dailySalaryInputs.orDragDrop')}</span>
                        </div>
                        <p className="text-xs text-gray-400">{t('dailySalaryInputs.supportedFormats')}</p>
                    </label>
                </div>

                {/* Data Grid Table */}
                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-bold text-lg text-gray-800">{t('dailySalaryInputs.employeeList')}</h3>
                        <span className="bg-indigo-100 text-indigo-800 text-xs px-2.5 py-1 rounded-full font-semibold">
                            {filteredInputs.length === inputs.length
                                ? t('dailySalaryInputs.collaboratorsCount', { count: inputs.length })
                                : t('dailySalaryInputs.filteredCount', { filtered: filteredInputs.length, total: inputs.length })
                            }
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
                                    <th className="px-6 py-4">{t('dailySalaryInputs.matricule')}</th>
                                    <th className="px-6 py-4">{t('payslipsList.employee')}</th>
                                    <th className="px-6 py-4">{t('dailySalaryInputs.workedDays')}</th>
                                    <th className="px-6 py-4">{t('dailySalaryInputs.monthGrossInput')}</th>
                                    <th className="px-6 py-4 text-gray-400">{t('dailySalaryInputs.contractBase')}</th>
                                    <th className="px-6 py-4 text-center">{t('dailySalaryInputs.status')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-10">
                                            <span className="animate-spin inline-block h-8 w-8 border-4 border-gray-200 border-t-indigo-600 rounded-full"></span>
                                            <p className="text-gray-500 mt-2">{t('dailySalaryInputs.loadingData')}</p>
                                        </td>
                                    </tr>
                                ) : filteredInputs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-10 text-gray-500">
                                            {t('dailySalaryInputs.noEmployeeFound')}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedInputs.map((item) => (
                                        <tr key={item.employee_id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-semibold text-gray-700">
                                                <span className="bg-gray-100 px-2.5 py-1 rounded text-xs">
                                                    {item.matricule}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-800">{item.name}</td>
                                            <td className="px-6 py-4">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={getDaysInMonth(selectedMonth)}
                                                    step="0.5"
                                                    className={`w-24 px-3 py-1.5 border rounded-lg text-center font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${item.is_modified ? "border-amber-400 bg-amber-50 text-amber-900" : "border-gray-300"
                                                        }`}
                                                    value={item.worked_days}
                                                    onChange={(e) => handleValueChange(item.employee_id, "worked_days", e.target.value)}
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="100"
                                                    className={`w-36 px-3 py-1.5 border rounded-lg font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${item.is_modified ? "border-amber-400 bg-amber-50 text-amber-900" : "border-gray-300"
                                                        }`}
                                                    value={item.gross_salary_input}
                                                    onChange={(e) => handleValueChange(item.employee_id, "gross_salary_input", e.target.value)}
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-gray-400 font-medium">
                                                {item.contract_base_salary.toLocaleString(dateLocale)} FCFA
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {item.is_modified ? (
                                                    <span className="bg-amber-100 text-amber-800 text-xs px-2.5 py-1 rounded-full font-bold">
                                                        {t('dailySalaryInputs.modifiedBadge')}
                                                    </span>
                                                ) : (
                                                    <span className="bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full font-medium">
                                                        {t('dailySalaryInputs.defaultBadge')}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex justify-between items-center p-4 bg-gray-50 border-t flex-wrap gap-4">
                            <div className="text-sm text-gray-600">
                                {t('dailySalaryInputs.showing', {
                                    from: Math.min(filteredInputs.length, (currentPage - 1) * itemsPerPage + 1),
                                    to: Math.min(filteredInputs.length, currentPage * itemsPerPage),
                                    total: filteredInputs.length
                                })}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    className="px-3 py-1.5 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-semibold"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                >
                                    {t('dailySalaryInputs.previous')}
                                </button>
                                <span className="px-3 py-1.5 text-sm text-gray-700 font-bold">
                                    {t('dailySalaryInputs.pageOf', { current: currentPage, total: totalPages })}
                                </span>
                                <button
                                    type="button"
                                    className="px-3 py-1.5 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-semibold"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                >
                                    {t('dailySalaryInputs.next')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </>
    );
};

export default DailySalaryInputs;
